import { groupsAfterAdditionUndo } from './courseAdditionUndoModel';
import { extractBaseIdFromSectionId, normalizeBaseId, normalizeRutgersBaseCourseId } from '../lib/courseIdFormatter';
import type { ScheduleContextAuthority, ScheduleWriteOperation } from './useScheduleWriteAuthority';

type Updates = { classes?: string[]; configuration?: unknown; is_public?: boolean };
export interface ScheduleDraftWrite { scheduleId: string; updates: Updates; makePrimary?: boolean }
interface SaveDependencies {
  writeSchedule: (id: string, updates: Updates) => Promise<unknown>;
  writePrimary: (id: string) => Promise<unknown>;
  publish: (identity: ScheduleWriteOperation['identity'], updates: Updates, primary: boolean, isLifetimeCurrent: () => boolean) => void;
  onError: (error: unknown, isReceiptCurrent: () => boolean) => void;
}
/** One admitted physical chain, with no local publication before its last step. */
export async function persistScheduleDraft(operation: ScheduleWriteOperation, data: ScheduleDraftWrite, deps: SaveDependencies): Promise<boolean> {
  if (!operation.isCurrent() || data.scheduleId !== operation.identity.scheduleId) return false;
  const updates = JSON.parse(JSON.stringify(data.updates)) as Updates;
  try {
    await deps.writeSchedule(data.scheduleId, JSON.parse(JSON.stringify(updates)) as Updates);
    if (!operation.isCurrent()) return false;
    if (data.makePrimary) {
      await deps.writePrimary(data.scheduleId);
      if (!operation.isCurrent()) return false;
    }
    deps.publish(operation.identity, updates, data.makePrimary === true, operation.isLifetimeCurrent);
    return true;
  } catch (error) { if (operation.isCurrent()) deps.onError(error, operation.isLifetimeCurrent); return false; }
}

interface CalendarDependencies {
  writePublic: (id: string) => Promise<unknown>;
  copy: (url: string) => Promise<unknown>;
  publishPublic: (identity: ScheduleWriteOperation['identity'], isLifetimeCurrent: () => boolean) => void;
  onCopied: (madePublic: boolean) => void;
  onError: (error: unknown, isReceiptCurrent: () => boolean) => void;
}
/** Clipboard and privacy receipts belong to the initiating full draft lifetime. */
export async function shareScheduleCalendar(operation: ScheduleWriteOperation, isPublic: boolean, origin: string, deps: CalendarDependencies): Promise<boolean> {
  if (!operation.isCurrent()) return false;
  let madePublic = false;
  try {
    if (!isPublic) {
      await deps.writePublic(operation.identity.scheduleId); madePublic = true;
      if (!operation.isCurrent()) return false;
    }
    await deps.copy(`${origin}/api/export/ics/${operation.identity.scheduleId}`);
    if (!operation.isCurrent()) return false;
    if (madePublic) deps.publishPublic(operation.identity, operation.isLifetimeCurrent);
    deps.onCopied(madePublic); return true;
  } catch (error) {
    if (operation.isCurrent()) {
      // A clipboard failure does not undo the acknowledged privacy write.
      if (madePublic) deps.publishPublic(operation.identity, operation.isLifetimeCurrent);
      deps.onError(error, operation.isLifetimeCurrent);
    }
    return false;
  }
}

export function removalCourseId(value: string): string {
  const compactSection = value.match(/^(\d{8}):\d+$/);
  return normalizeRutgersBaseCourseId(value) ?? normalizeBaseId(compactSection ? compactSection[1] : value);
}
interface CatalogSection { id: string; baseId: string }
export function classesAfterCourseRemoval(classes: readonly string[], courseId: string, catalog: readonly CatalogSection[]): string[] {
  const target = removalCourseId(courseId);
  const sections = catalog.filter(course => normalizeBaseId(course.baseId) === target);
  const ids = new Set(sections.flatMap(course => [course.id, `${course.baseId}:${course.id}`]));
  return classes.filter(id => !ids.has(id) && removalCourseId(extractBaseIdFromSectionId(id)) !== target);
}
interface RemovalDependencies {
  removeSelected: (courseId: string, isIntentCurrent: () => boolean) => void;
  loadCatalog: (term: string) => Promise<readonly CatalogSection[]>;
  removeClasses: (identity: ScheduleContextAuthority['identity'], courseId: string, catalog: readonly CatalogSection[], isIntentCurrent: () => boolean) => void;
  onError: (error: unknown, isReceiptCurrent: () => boolean) => void;
}
/** Removal is a compositional draft edit; existing autosave owns persistence. */
export async function removeScheduleCourse(authority: ScheduleContextAuthority, courseId: string, deps: RemovalDependencies): Promise<boolean> {
  if (!authority.isContextCurrent()) return false;
  const target = removalCourseId(courseId);
  deps.removeSelected(target, authority.isContextCurrent);
  try {
    const catalog = await deps.loadCatalog(authority.identity.term);
    if (!authority.isContextCurrent()) return false;
    deps.removeClasses(authority.identity, target, catalog, authority.isContextCurrent); return true;
  } catch (error) { if (authority.isContextCurrent()) deps.onError(error, authority.isContextCurrent); return false; }
}

/** Only the owned Add receipt calls this draft inverse; normal Remove is unchanged.
 * True admits a queued edit. Only a matching committed selected-groups render
 * proves application; the pure functional updater can still decline drift. */
export function undoScheduleCourseAddition(authority: ScheduleContextAuthority,
  inverse: import('./courseAdditionUndoModel').CourseAdditionInverse,
  deps: { setSelectedCourses: import('react').Dispatch<import('react').SetStateAction<import('../types/course').GroupedCourse[]>>; markChanged: () => void }): boolean {
  if (!authority.isContextCurrent()) return false;
  deps.setSelectedCourses(previous => authority.isContextCurrent() ? groupsAfterAdditionUndo(previous, inverse) ?? previous : previous);
  if (!authority.isContextCurrent()) return false;
  deps.markChanged(); return true;
}
